import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import {
  PlusIcon, MegaphoneIcon, XMarkIcon, BellIcon, UserGroupIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Select from 'react-select';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .notices-root { font-family:'Sora',sans-serif; color:#1a1d2e; }

  .notices-page-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:1.75rem; flex-wrap:wrap; gap:12px; }
  .notices-page-title  { font-size:1.6rem; font-weight:700; letter-spacing:-.03em; color:#0f1123; }
  .notices-page-sub    { font-size:.78rem; color:#9094a8; margin-top:2px; }

  .btn { padding:8px 18px; border-radius:10px; font-size:.8rem; font-weight:600; font-family:'Sora',sans-serif; cursor:pointer; border:none; display:inline-flex; align-items:center; gap:7px; transition:transform .15s,box-shadow .15s; }
  .btn:active { transform:scale(.97); }
  .btn svg { width:15px; height:15px; stroke-width:2; flex-shrink:0; }
  .btn-blue  { background:#3b6ef8; color:white; }
  .btn-blue:hover  { box-shadow:0 4px 14px rgba(59,110,248,.35); }
  .btn-ghost { background:#f0f2f7; color:#6b7194; border:1px solid #e0e4f0; }
  .btn-ghost:hover { background:#e8ebf4; }

  .notices-empty { text-align:center; padding:64px 20px; color:#b0b5cc; }
  .notices-empty-icon { width:48px; height:48px; margin:0 auto 14px; background:#f0f2f7; border-radius:14px; display:flex; align-items:center; justify-content:center; color:#c8ccde; }
  .notices-empty-icon svg { width:22px; height:22px; stroke-width:1.5; }
  .notices-empty-title { font-size:.9rem; font-weight:600; color:#9094a8; }
  .notices-empty-sub   { font-size:.76rem; margin-top:4px; }

  .notices-list { display:flex; flex-direction:column; gap:12px; }
  .notice-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:20px 22px; transition:box-shadow .18s,transform .18s; animation:noticeIn .35s ease both; position:relative; overflow:hidden; }
  .notice-card::before { content:''; position:absolute; left:0; top:0; bottom:0; width:4px; background:linear-gradient(180deg,#3b6ef8,#8b5cf6); border-radius:4px 0 0 4px; }
  .notice-card:hover { box-shadow:0 6px 24px rgba(0,0,0,.07); transform:translateY(-1px); }
  @keyframes noticeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }

  .notice-card-top  { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; }
  .notice-card-body { flex:1; min-width:0; }
  .notice-title   { font-size:.95rem; font-weight:700; color:#0f1123; letter-spacing:-.01em; margin-bottom:6px; }
  .notice-content { font-size:.8rem; color:#6b7194; line-height:1.65; margin-bottom:12px; }
  .notice-meta    { display:flex; flex-wrap:wrap; align-items:center; gap:10px; }
  .notice-meta-item { display:flex; align-items:center; gap:5px; font-size:.72rem; color:#9094a8; }
  .notice-meta-item svg { width:12px; height:12px; stroke-width:1.8; }
  .notice-meta-dot { width:3px; height:3px; border-radius:50%; background:#d8dce8; }

  .audience-tags { display:flex; flex-wrap:wrap; gap:4px; }
  .audience-tag  { padding:2px 8px; border-radius:99px; font-size:.65rem; font-weight:700; text-transform:capitalize; letter-spacing:.03em; }
  .aud-admin  { background:#eef2ff; color:#3b6ef8; }
  .aud-teacher{ background:#ecfdf5; color:#10c98e; }
  .aud-student{ background:#f3f0ff; color:#8b5cf6; }
  .aud-parent { background:#fffbeb; color:#f59e0b; }
  .aud-finance{ background:#f0fdfa; color:#0d9488; }
  .aud-coordinator{ background:#fff7ed; color:#f97316; }
  .aud-default{ background:#f0f2f7; color:#6b7194; }

  .mode-tabs { display:flex; gap:6px; }
  .mode-tab  { flex:1; padding:9px 12px; border-radius:9px; border:1.5px solid #e0e4f0; background:#f8f9fc; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; color:#6b7194; cursor:pointer; text-align:center; }
  .mode-tab.active { background:#3b6ef8; border-color:#3b6ef8; color:white; }
  .form-select { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-select:focus { border-color:#3b6ef8; background:white; }
  .form-row-2 { display:grid; grid-template-columns:1fr 1fr; gap:10px; }

  .notice-delete { width:30px; height:30px; border-radius:8px; border:1px solid #e8ebf4; background:#f8f9fc; display:flex; align-items:center; justify-content:center; cursor:pointer; color:#c0c5dc; transition:background .13s,border-color .13s,color .13s; flex-shrink:0; }
  .notice-delete:hover { background:#fff1f1; border-color:#fecaca; color:#ef4444; }
  .notice-delete svg { width:14px; height:14px; stroke-width:1.8; }

  .poster-avatar { width:18px; height:18px; border-radius:5px; background:linear-gradient(135deg,#3b6ef8,#8b5cf6); display:inline-flex; align-items:center; justify-content:center; font-size:.52rem; font-weight:700; color:white; flex-shrink:0; }
  .notice-time { font-family:'JetBrains Mono',monospace; font-size:.7rem; color:#b0b5cc; }

  .modal-overlay { position:fixed; inset:0; background:rgba(10,12,28,.55); backdrop-filter:blur(3px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; animation:overlayIn .2s ease; }
  @keyframes overlayIn { from{opacity:0} to{opacity:1} }
  .modal-box { background:white; border-radius:20px; width:100%; max-width:520px; border:1px solid #e8ebf4; box-shadow:0 24px 64px rgba(0,0,0,.18); animation:modalIn .25s ease; overflow:hidden; display:flex; flex-direction:column; max-height:90vh; }
  @keyframes modalIn { from{opacity:0;transform:translateY(16px) scale(.97)} to{opacity:1;transform:translateY(0) scale(1)} }
  .modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; flex-shrink:0; gap:8px; }
  .modal-header-left { display:flex; align-items:center; gap:10px; min-width:0; }
  .modal-icon { width:34px; height:34px; border-radius:10px; background:#eef2ff; color:#3b6ef8; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .modal-icon svg { width:17px; height:17px; stroke-width:1.8; }
  .modal-title { font-size:.95rem; font-weight:700; color:#0f1123; }
  .modal-sub   { font-size:.72rem; color:#9094a8; margin-top:1px; }
  .modal-close { width:30px; height:30px; border-radius:8px; border:1px solid #e8ebf4; background:#f8f9fc; display:flex; align-items:center; justify-content:center; cursor:pointer; color:#9094a8; flex-shrink:0; }
  .modal-close:hover { background:#f0f2f7; color:#1a1d2e; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-body { padding:22px; overflow-y:auto; flex:1; display:flex; flex-direction:column; gap:16px; }
  .modal-footer { display:flex; justify-content:flex-end; gap:8px; padding:16px 22px; border-top:1px solid #f0f2f8; flex-shrink:0; flex-wrap:wrap; }

  .form-field { display:flex; flex-direction:column; gap:6px; }
  .form-label { font-size:.7rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:#f8f9fc; color:#1a1d2e; outline:none; transition:border-color .15s,background .15s,box-shadow .15s; width:100%; }
  .form-textarea { resize:vertical; min-height:100px; line-height:1.6; }
  .form-input:focus,.form-textarea:focus { border-color:#3b6ef8; background:white; box-shadow:0 0 0 3px rgba(59,110,248,.08); }

  .notif-toggle { display:flex; align-items:center; gap:12px; padding:12px 14px; border-radius:12px; border:1.5px solid #e0e4f0; background:#f8f9fc; cursor:pointer; transition:border-color .15s,background .15s; user-select:none; }
  .notif-toggle:hover { border-color:#c7d2fe; background:#f0f4ff; }
  .notif-toggle input { display:none; }
  .notif-toggle-icon { width:32px; height:32px; border-radius:9px; background:#eef2ff; display:flex; align-items:center; justify-content:center; color:#3b6ef8; flex-shrink:0; }
  .notif-toggle-icon svg { width:15px; height:15px; stroke-width:1.8; }
  .notif-toggle-text  { flex:1; min-width:0; }
  .notif-toggle-label { font-size:.8rem; font-weight:600; color:#1a1d2e; }
  .notif-toggle-sub   { font-size:.7rem; color:#9094a8; margin-top:1px; }
  .toggle-track { width:36px; height:20px; background:#e0e4f0; border-radius:99px; position:relative; transition:background .2s; flex-shrink:0; }
  .toggle-thumb { position:absolute; top:3px; left:3px; width:14px; height:14px; background:white; border-radius:50%; transition:transform .2s; box-shadow:0 1px 3px rgba(0,0,0,.15); }
  .notif-toggle input:checked + .notif-toggle-icon { background:#3b6ef8; color:white; }
  .notif-toggle input:checked ~ .toggle-track { background:#3b6ef8; }
  .notif-toggle input:checked ~ .toggle-track .toggle-thumb { transform:translateX(16px); }

  .rselect__control { font-family:'Sora',sans-serif !important; font-size:.82rem !important; border:1.5px solid #e0e4f0 !important; border-radius:10px !important; background:#f8f9fc !important; box-shadow:none !important; min-height:40px !important; }
  .rselect__control:hover { border-color:#c7d2fe !important; }
  .rselect__control--is-focused { border-color:#3b6ef8 !important; background:white !important; box-shadow:0 0 0 3px rgba(59,110,248,.08) !important; }
  .rselect__multi-value { background:#eef2ff !important; border-radius:6px !important; }
  .rselect__multi-value__label { color:#3b6ef8 !important; font-size:.72rem !important; font-weight:600 !important; font-family:'Sora',sans-serif !important; }
  .rselect__multi-value__remove:hover { background:#dbe4ff !important; color:#3b6ef8 !important; }
  .rselect__option--is-focused  { background:#f0f4ff !important; }
  .rselect__option--is-selected { background:#3b6ef8 !important; }
  .rselect__placeholder { font-size:.8rem !important; color:#b0b5cc !important; }
  .rselect__menu { border-radius:12px !important; border:1px solid #e0e4f0 !important; box-shadow:0 8px 24px rgba(0,0,0,.1) !important; }
  .rselect__menu-list { padding:6px !important; }
  .rselect__option { border-radius:8px !important; font-family:'Sora',sans-serif !important; font-size:.8rem !important; }

  .notices-fade { animation:noticesFadeUp .4s ease both; }
  @keyframes noticesFadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }

  /* ── Mobile ── */
  @media (max-width:768px) {
    .notices-page-title { font-size:1.2rem; }
    .notices-page-header { margin-bottom:1.2rem; }

    /* Notice card tighter padding */
    .notice-card { padding:14px 16px 14px 18px; }
    .notice-title { font-size:.88rem; }
    .notice-content { font-size:.76rem; margin-bottom:8px; }

    /* Meta wraps cleanly */
    .notice-meta { gap:6px; }
    .notice-meta-dot { display:none; }

    /* Modal */
    .modal-body { padding:16px; gap:12px; }
    .modal-footer { justify-content:stretch; }
    .modal-footer .btn { flex:1; justify-content:center; }

    /* Notif toggle: hide sub text on very small */
    .notif-toggle { padding:10px 12px; gap:8px; }
  }

  @media (max-width:400px) {
    .notif-toggle-sub { display:none; }
    .notice-time { font-size:.64rem; }
  }
`;

const audienceOptions = [
  { value:'admin',       label:'Admin'       },
  { value:'teacher',     label:'Teacher'     },
  { value:'student',     label:'Student'     },
  { value:'parent',      label:'Parent'      },
  { value:'finance',     label:'Finance'     },
  { value:'coordinator', label:'Coordinator' },
];

const audienceClass = { admin:'aud-admin', teacher:'aud-teacher', student:'aud-student', parent:'aud-parent', finance:'aud-finance', coordinator:'aud-coordinator' };

// Roles that can be targeted individually, and where to fetch the picker list from.
const SPECIFIC_ROLES = [
  { value:'teacher',     label:'Teacher'     },
  { value:'coordinator', label:'Coordinator' },
  { value:'finance',     label:'Finance'     },
  { value:'student',     label:'Student'     },
];

const getInitials = (name='') => name.split(' ').map(n=>n[0]).join('').toUpperCase().slice(0,2)||'A';
const formatDate  = (d) => new Date(d).toLocaleString('en-US',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'});

const EMPTY_FORM = { title:'', content:'', audience:[], sendNotification:false, attachments:[] };

const Notices = () => {
  const [notices,   setNotices]   = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData,  setFormData]  = useState(EMPTY_FORM);

  // Specific-person targeting
  const [mode,          setMode]          = useState('broadcast'); // 'broadcast' | 'specific'
  const [specificRole,  setSpecificRole]  = useState('teacher');
  const [people,        setPeople]        = useState([]);
  const [targetUserId,  setTargetUserId]  = useState('');
  const [classes,       setClasses]       = useState([]);
  const [filterClass,   setFilterClass]   = useState('');
  const [filterSection, setFilterSection] = useState('');

  useEffect(() => { fetchNotices(); }, []);
  useEffect(() => { api.get('/admin/classes').then(r => setClasses(r.data || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (mode !== 'specific') return;
    setTargetUserId('');
    if (specificRole === 'teacher') {
      api.get('/admin/teachers?limit=500').then(r => setPeople(r.data?.teachers || [])).catch(() => setPeople([]));
    } else if (specificRole === 'coordinator') {
      api.get('/admin/coordinators').then(r => setPeople(r.data || [])).catch(() => setPeople([]));
    } else if (specificRole === 'finance') {
      api.get('/admin/finance-members-full').then(r => setPeople(r.data || [])).catch(() => setPeople([]));
    } else if (specificRole === 'student') {
      setPeople([]); // loaded by the class/section effect below
    }
  }, [mode, specificRole]);

  useEffect(() => {
    if (mode !== 'specific' || specificRole !== 'student') return;
    setTargetUserId('');
    if (!filterClass) { setPeople([]); return; }
    const params = new URLSearchParams({ classId: filterClass, limit: '200' });
    if (filterSection) params.set('section', filterSection);
    api.get(`/admin/students?${params}`).then(r => setPeople(r.data?.students || [])).catch(() => setPeople([]));
  }, [mode, specificRole, filterClass, filterSection]);

  const fetchNotices = async () => {
    try { const {data}=await api.get('/admin/notices'); setNotices(data); }
    catch { toast.error('Failed to fetch notices'); }
  };

  const personValue = (p) => p.user?._id || p._id;
  const personLabel = (p) => `${p.user?.name || p.name} — ${p.user?.email || p.email}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.content) { toast.error('Title and content required'); return; }
    if (mode === 'specific' && !targetUserId) { toast.error('Select a person'); return; }
    try {
      const payload = mode === 'specific'
        ? { title: formData.title, content: formData.content, audience: [specificRole], targetUsers: [targetUserId] }
        : formData;
      await api.post('/admin/notices', payload);
      toast.success('Notice posted');
      setShowModal(false);
      fetchNotices();
    } catch { toast.error('Failed to create notice'); }
  };

  const openModal = () => {
    setFormData(EMPTY_FORM);
    setMode('broadcast'); setSpecificRole('teacher'); setTargetUserId('');
    setFilterClass(''); setFilterSection('');
    setShowModal(true);
  };

  return (
    <>
      <style>{style}</style>
      <div className="notices-root notices-fade">

        <div className="notices-page-header">
          <div>
            <h1 className="notices-page-title">Noticeboard</h1>
            <p className="notices-page-sub">Post and manage announcements for students, teachers and parents.</p>
          </div>
          <button className="btn btn-blue" onClick={openModal}><PlusIcon />Post Notice</button>
        </div>

        {notices.length===0 ? (
          <div className="notices-empty">
            <div className="notices-empty-icon"><MegaphoneIcon /></div>
            <div className="notices-empty-title">No notices yet</div>
            <div className="notices-empty-sub">Post your first notice to get started.</div>
          </div>
        ) : (
          <div className="notices-list">
            {notices.map((notice,i) => (
              <div key={notice._id} className="notice-card" style={{animationDelay:`${i*.05}s`}}>
                <div className="notice-card-top">
                  <div className="notice-card-body">
                    <div className="notice-title">{notice.title}</div>
                    <div className="notice-content">{notice.content}</div>
                    <div className="notice-meta">
                      <div className="notice-meta-item">
                        <div className="poster-avatar">{getInitials(notice.createdBy?.name)}</div>
                        {notice.createdBy?.name||'Admin'}
                      </div>
                      <div className="notice-meta-dot" />
                      <div className="notice-meta-item">
                        <UserGroupIcon />
                        <div className="audience-tags">
                          {notice.audience.map(aud=>(
                            <span key={aud} className={`audience-tag ${audienceClass[aud]||'aud-default'}`}>{aud}</span>
                          ))}
                        </div>
                      </div>
                      <div className="notice-meta-dot" />
                      <div className="notice-meta-item">
                        <span className="notice-time">{formatDate(notice.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && createPortal(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal-box">
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-icon"><MegaphoneIcon /></div>
                <div>
                  <div className="modal-title">Post New Notice</div>
                  <div className="modal-sub">Visible to the selected audience.</div>
                </div>
              </div>
              <button className="modal-close" onClick={()=>setShowModal(false)}><XMarkIcon /></button>
            </div>
            <form onSubmit={handleSubmit} style={{display:'contents'}}>
              <div className="modal-body">
                <div className="form-field">
                  <label className="form-label">Title</label>
                  <input type="text" className="form-input" placeholder="e.g. School Closed on Friday" value={formData.title} onChange={e=>setFormData({...formData,title:e.target.value})} required />
                </div>
                <div className="form-field">
                  <label className="form-label">Content</label>
                  <textarea className="form-textarea" placeholder="Write the full notice here…" value={formData.content} onChange={e=>setFormData({...formData,content:e.target.value})} required />
                </div>
                <div className="form-field">
                  <label className="form-label">Send To</label>
                  <div className="mode-tabs">
                    <div className={`mode-tab ${mode==='broadcast'?'active':''}`} onClick={()=>setMode('broadcast')}>Broadcast</div>
                    <div className={`mode-tab ${mode==='specific'?'active':''}`} onClick={()=>setMode('specific')}>Specific Person</div>
                  </div>
                </div>

                {mode === 'broadcast' ? (
                  <div className="form-field">
                    <label className="form-label">Audience</label>
                    <Select isMulti options={audienceOptions} classNamePrefix="rselect" placeholder="Select who can see this…"
                      value={audienceOptions.filter(opt=>formData.audience.includes(opt.value))}
                      onChange={selected=>setFormData({...formData,audience:selected.map(s=>s.value)})} />
                  </div>
                ) : (
                  <>
                    <div className="form-field">
                      <label className="form-label">Role</label>
                      <select className="form-select" value={specificRole} onChange={e=>setSpecificRole(e.target.value)}>
                        {SPECIFIC_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </div>
                    {specificRole === 'student' && (
                      <div className="form-row-2">
                        <div className="form-field">
                          <label className="form-label">Class</label>
                          <select className="form-select" value={filterClass} onChange={e=>{setFilterClass(e.target.value); setFilterSection('');}}>
                            <option value="">Select class…</option>
                            {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                          </select>
                        </div>
                        <div className="form-field">
                          <label className="form-label">Section</label>
                          <select className="form-select" value={filterSection} onChange={e=>setFilterSection(e.target.value)} disabled={!filterClass}>
                            <option value="">All sections</option>
                            {(classes.find(c=>String(c._id)===String(filterClass))?.sections||[]).map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                      </div>
                    )}
                    <div className="form-field">
                      <label className="form-label">Person</label>
                      <select className="form-select" value={targetUserId} onChange={e=>setTargetUserId(e.target.value)}
                        disabled={specificRole==='student' && !filterClass}>
                        <option value="">{people.length ? 'Select…' : (specificRole==='student' && !filterClass ? 'Select a class first' : 'No results')}</option>
                        {people.map(p => <option key={personValue(p)} value={personValue(p)}>{personLabel(p)}</option>)}
                      </select>
                    </div>
                  </>
                )}
                <div className="form-field">
                  <label className="form-label">Notifications</label>
                  <label className="notif-toggle">
                    <input type="checkbox" checked={formData.sendNotification} onChange={e=>setFormData({...formData,sendNotification:e.target.checked})} />
                    <div className="notif-toggle-icon"><BellIcon /></div>
                    <div className="notif-toggle-text">
                      <div className="notif-toggle-label">Send Email / SMS notifications</div>
                      <div className="notif-toggle-sub">Notify selected audience immediately.</div>
                    </div>
                    <div className="toggle-track"><div className="toggle-thumb" /></div>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={()=>setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-blue"><MegaphoneIcon />Post Notice</button>
              </div>
            </form>
          </div>
        </div>
      
      , document.body)}
    </>
  );
};

export default Notices;